"use client";

import { useEffect, useState } from "react";
import {
  X, Copy, Check, ShieldCheck, Star, Clock, ExternalLink, AlertCircle,
  Tag, Truck, Gift, Percent, BadgeCheck, Receipt, Info,
} from "lucide-react";
import type { Coupon } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StoreLogo } from "./store-logo";
import { GoLink } from "@/components/go-link";
import { formatRelativeTime } from "@/lib/utils";

interface Props {
  coupon: Coupon | null;
  onClose: () => void;
  revealStore?: boolean;
}

export function CouponPopup({ coupon, onClose, revealStore = true }: Props) {
  const [copied, setCopied] = useState(false);
  const [storeOpened, setStoreOpened] = useState(false);

  useEffect(() => {
    if (!coupon) return;
    setCopied(false);
    setStoreOpened(false);

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    if (revealStore) {
      const opened = window.open("about:blank", "_blank");
      setStoreOpened(!!opened);
      if (opened && coupon.storeName) {
        const store = MOCK_STORE_URL_MAP[coupon.storeId] ?? `https://www.google.com/search?q=${encodeURIComponent(coupon.storeName)}`;
        // Clic de cupón por /go/<id> (origen "coupon") para que la pantalla intermedia
        // diga "Cupón activado". Si no se puede registrar, va directo a la tienda.
        fetch("/api/go", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId: coupon.storeId, section: "coupon", url: store }),
        })
          .then((r) => r.json())
          .then(({ id }: { id?: number }) => {
            opened.location.href = typeof id === "number" ? `/go/${id}` : store;
          })
          .catch(() => {
            try {
              opened.location.href = store;
            } catch {
              // ignore
            }
          });
      }
      if (coupon.code) {
        navigator.clipboard?.writeText(coupon.code).then(() => setCopied(true)).catch(() => {});
      }
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [coupon, onClose, revealStore]);

  if (!coupon) return null;

  const copy = async () => {
    if (!coupon.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
    } catch {
      // ignore
    }
  };

  const features = buildFeatures(coupon);

  return (
    <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-background w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up relative max-h-[92dvh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background/90 border hover:bg-muted flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="h-28 bg-linear-to-br from-green-500 via-emerald-500 to-teal-600 flex items-center justify-center relative">
          <div className="absolute bottom-0 translate-y-1/2 left-6">
            <StoreLogo name={coupon.storeName} logo={coupon.storeLogo} size={80} rounded="2xl" className="shadow-md" />
          </div>
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
            {coupon.isExclusive && (
              <Badge className="bg-white text-fuchsia-700 border-0 text-[10px] h-5">
                <Star className="w-3 h-3 mr-0.5 fill-fuchsia-700" /> Exclusivo SuCom
              </Badge>
            )}
            {coupon.isVerified && (
              <Badge className="bg-white/90 text-emerald-700 border-0 text-[10px] h-5">
                <ShieldCheck className="w-3 h-3 mr-0.5" /> Verificado
              </Badge>
            )}
          </div>
        </div>

        <div className="pt-14 px-6 pb-6">
          <p className="text-xs text-muted-foreground">{coupon.storeName}</p>
          <h3 className="text-2xl font-black leading-tight mt-1">{coupon.title}</h3>
          {coupon.description && (
            <p className="text-sm text-muted-foreground mt-2">{coupon.description}</p>
          )}

          {coupon.expiresAt && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Expira {formatRelativeTime(coupon.expiresAt)}
            </p>
          )}

          {coupon.code ? (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                Tu código
              </p>
              <button
                onClick={copy}
                className="w-full flex items-center justify-between gap-2 border-2 border-dashed border-emerald-500 bg-emerald-50 rounded-xl px-4 py-4 hover:bg-emerald-100 transition-colors"
              >
                <span className="font-mono font-black text-xl text-emerald-800 tracking-wider">
                  {coupon.code}
                </span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" /> ¡Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Copiar
                    </>
                  )}
                </span>
              </button>
              {storeOpened && revealStore && (
                <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1.5">
                  <AlertCircle className="w-3 h-3 mt-0.5 text-emerald-600" />
                  Ya abrimos la tienda en una nueva pestaña con tu código copiado.
                  Sólo pégalo al pagar.
                </p>
              )}
            </div>
          ) : (
            <div className="mt-5">
              <Button
                asChild
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                <GoLink
                  storeId={coupon.storeId}
                  storeDbId={coupon.storeId}
                  url={MOCK_STORE_URL_MAP[coupon.storeId]}
                  tab="coupon"
                >
                  Obtener oferta <ExternalLink className="w-4 h-4 ml-1" />
                </GoLink>
              </Button>
              <p className="text-[11px] text-muted-foreground mt-2 text-center">
                No requiere código — aplica automáticamente al comprar.
              </p>
            </div>
          )}

          {features.length > 0 && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                Características del cupón
              </p>
              <ul className="divide-y rounded-xl border bg-muted/30 overflow-hidden">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 px-3 py-2.5">
                    <f.icon className={`w-4 h-4 mt-0.5 shrink-0 ${f.color}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-muted-foreground">{f.label}</p>
                      <p className="text-sm font-semibold leading-tight">{f.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {coupon.terms && (
            <div className="mt-4 text-[11px] text-muted-foreground border rounded-xl p-3 bg-muted/30">
              <p className="font-semibold text-foreground mb-1 flex items-center gap-1">
                <Info className="w-3 h-3" /> Términos y condiciones
              </p>
              <p>{coupon.terms}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function buildFeatures(c: Coupon) {
  const out: { icon: typeof Tag; label: string; value: string; color: string }[] = [];

  if (c.discount != null && c.discount > 0) {
    const val =
      c.discountType === "FIXED"
        ? `$${c.discount.toLocaleString("es-MX")} MXN`
        : `${c.discount}%`;
    out.push({ icon: Percent, label: "Descuento", value: val, color: "text-emerald-600" });
  }

  if (c.type === "FREE_SHIPPING") {
    out.push({ icon: Truck, label: "Tipo", value: "Envío gratis", color: "text-sky-600" });
  } else if (c.type === "GIFT") {
    out.push({ icon: Gift, label: "Tipo", value: "Regalo incluido", color: "text-fuchsia-600" });
  } else if (c.type === "AUTOMATIC") {
    out.push({ icon: BadgeCheck, label: "Aplicación", value: "Automática al pagar", color: "text-indigo-600" });
  } else if (c.type === "CODE") {
    out.push({ icon: Tag, label: "Aplicación", value: "Con código al checkout", color: "text-emerald-600" });
  }

  if (c.minPurchase) {
    out.push({
      icon: Receipt,
      label: "Compra mínima",
      value: `$${c.minPurchase.toLocaleString("es-MX")} MXN`,
      color: "text-amber-600",
    });
  }

  if (c.maxDiscount) {
    out.push({
      icon: BadgeCheck,
      label: "Tope de descuento",
      value: `$${c.maxDiscount.toLocaleString("es-MX")} MXN`,
      color: "text-amber-600",
    });
  }

  if (c.isExclusive) {
    out.push({
      icon: Star,
      label: "Disponibilidad",
      value: "Exclusivo miembros SuCom",
      color: "text-fuchsia-600",
    });
  }

  if (c.isVerified) {
    out.push({
      icon: ShieldCheck,
      label: "Verificación",
      value: "Revisado por el equipo SuCom",
      color: "text-emerald-600",
    });
  }

  return out;
}

const MOCK_STORE_URL_MAP: Record<string, string> = {
  walmart: "https://walmart.com.mx",
  amazon: "https://amazon.com.mx",
  mercadolibre: "https://mercadolibre.com.mx",
  liverpool: "https://liverpool.com.mx",
  aliexpress: "https://aliexpress.com",
  shein: "https://shein.com.mx",
  nike: "https://nike.com/mx",
  adidas: "https://adidas.mx",
  sephora: "https://sephora.com.mx",
  ulta: "https://ulta.com",
  mac: "https://maccosmetics.com.mx",
  bestbuy: "https://bestbuy.com.mx",
  apple: "https://apple.com/mx",
  samsung: "https://samsung.com/mx",
  hp: "https://hp.com/mx-es",
  dell: "https://dell.com.mx",
  booking: "https://booking.com",
  expedia: "https://expedia.com.mx",
  aviasales: "https://aviasales.com",
  despegar: "https://despegar.com.mx",
  "uber-eats": "https://ubereats.com",
  rappi: "https://rappi.com",
  didi: "https://didiglobal.com",
  netflix: "https://netflix.com/mx",
  spotify: "https://spotify.com",
  hbo: "https://max.com",
  "farma-guadalajara": "https://farmaciasguadalajara.com",
  "farma-ahorro": "https://fahorro.com",
  costco: "https://costco.com.mx",
  sams: "https://sams.com.mx",
  chedraui: "https://chedraui.com.mx",
  soriana: "https://soriana.com",
};
